import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import css from './HowItWorksSection.module.css';

// "How it works" — a tabbed buyer / seller walkthrough that mirrors the
// refreshed design mockup. Each step uses an inline illustration (no image
// assets needed) plus a number, title and short description. Uses the site's
// Gilroy heading font via the CSS module.

const BuyerFind = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <rect x="10" y="18" width="42" height="40" rx="5" fill="#FFFFFF" stroke="#081838" strokeWidth="2" />
    <rect x="14" y="22" width="34" height="20" rx="3" fill="#F0C77A" />
    <rect x="14" y="46" width="20" height="3" rx="1.5" fill="#34405E" />
    <rect x="14" y="51" width="14" height="3" rx="1.5" fill="#00A878" />
    <circle cx="54" cy="54" r="12" fill="#FFFFFF" stroke="#081838" strokeWidth="2.5" />
    <circle cx="54" cy="54" r="8" fill="#00C890" opacity="0.30" />
    <line x1="63" y1="63" x2="72" y2="72" stroke="#081838" strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

const BuyerSave = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <rect x="10" y="22" width="34" height="40" rx="5" fill="#FFFFFF" stroke="#081838" strokeWidth="2" />
    <rect x="14" y="26" width="26" height="18" rx="3" fill="#BFEEDC" />
    <rect x="14" y="48" width="20" height="3" rx="1.5" fill="#34405E" />
    <rect x="14" y="53" width="14" height="3" rx="1.5" fill="#00A878" />
    <circle cx="56" cy="36" r="18" fill="#FAF6EE" stroke="#081838" strokeWidth="2" />
    <path d="M56 28 C 51 22 42 26 42 33 C 42 42 56 50 56 50 C 56 50 70 42 70 33 C 70 26 61 22 56 28 Z" fill="#00C890" stroke="#081838" strokeWidth="2" />
  </svg>
);

const BuyerPay = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <rect x="6" y="22" width="50" height="32" rx="4" fill="#081838" />
    <rect x="6" y="28" width="50" height="5" fill="#00C890" />
    <rect x="14" y="40" width="20" height="3" rx="1.5" fill="#FAF6EE" opacity="0.7" />
    <rect x="14" y="46" width="12" height="3" rx="1.5" fill="#FAF6EE" opacity="0.5" />
    <path d="M58 38 C 50 38 46 44 46 52 C 46 62 52 68 58 70 C 64 68 70 62 70 52 C 70 44 66 38 58 38 Z" fill="#00C890" stroke="#081838" strokeWidth="2" />
    <path d="M52 52 L 57 57 L 66 48" stroke="#081838" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const BuyerReceive = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <path d="M12 30 L 40 18 L 68 30 L 68 56 L 40 68 L 12 56 Z" fill="#FAF6EE" stroke="#081838" strokeWidth="2" />
    <path d="M12 30 L 40 42 L 68 30" stroke="#081838" strokeWidth="2" />
    <line x1="40" y1="42" x2="40" y2="68" stroke="#081838" strokeWidth="2" />
    <rect x="26" y="24" width="28" height="5" fill="#00C890" />
    <path d="M40 50 C 37 46 32 49 32 53 C 32 57 40 61 40 61 C 40 61 48 57 48 53 C 48 49 43 46 40 50 Z" fill="#00C890" />
  </svg>
);

const BuyerReview = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <path d="M12 22 C 12 18 15 14 20 14 L 60 14 C 65 14 68 18 68 22 L 68 42 C 68 46 65 50 60 50 L 32 50 L 20 62 L 24 50 L 20 50 C 15 50 12 46 12 42 Z" fill="#FAF6EE" stroke="#081838" strokeWidth="2" />
    <g fill="#F0C77A" stroke="#081838" strokeWidth="1.4" strokeLinejoin="round">
      <path d="M24 32 L 26 27 L 28 32 L 33 32 L 29 36 L 30 41 L 26 38 L 22 41 L 23 36 L 19 32 Z" />
      <path d="M40 32 L 42 27 L 44 32 L 49 32 L 45 36 L 46 41 L 42 38 L 38 41 L 39 36 L 35 32 Z" />
      <path d="M56 32 L 58 27 L 60 32 L 65 32 L 61 36 L 62 41 L 58 38 L 54 41 L 55 36 L 51 32 Z" />
    </g>
  </svg>
);

const SellerList = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <rect x="22" y="14" width="38" height="54" rx="6" fill="#081838" />
    <rect x="26" y="20" width="30" height="40" rx="2" fill="#BFEEDC" />
    <circle cx="41" cy="34" r="4" fill="#00A878" />
    <path d="M30 44 L 38 36 L 48 46 L 56 38" stroke="#00A878" strokeWidth="2" />
    <circle cx="41" cy="64" r="2.5" fill="#FAF6EE" />
    <circle cx="60" cy="60" r="10" fill="#00C890" stroke="#081838" strokeWidth="2" />
    <path d="M60 56 L 60 64 M 56 60 L 64 60" stroke="#081838" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const SellerBank = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <rect x="8" y="24" width="46" height="34" rx="4" fill="#FAF6EE" stroke="#081838" strokeWidth="2" />
    <rect x="8" y="30" width="46" height="6" fill="#00C890" />
    <rect x="14" y="44" width="20" height="3" rx="1.5" fill="#081838" />
    <rect x="14" y="50" width="12" height="3" rx="1.5" fill="#34405E" />
    <circle cx="58" cy="40" r="14" fill="#F0C77A" stroke="#081838" strokeWidth="2" />
    <text x="58" y="46" fontFamily="'Gilroy-Bold', sans-serif" fontSize="16" fontWeight="800" fill="#081838" textAnchor="middle">£</text>
  </svg>
);

const SellerNotified = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <path d="M40 12 C 31 12 24 19 24 28 L 24 46 L 19 53 L 61 53 L 56 46 L 56 28 C 56 19 49 12 40 12 Z" fill="#F0C77A" stroke="#081838" strokeWidth="2" strokeLinejoin="round" />
    <path d="M36 53 C 36 56 38 58 40 58 C 42 58 44 56 44 53" stroke="#081838" strokeWidth="2" />
    <circle cx="58" cy="20" r="8" fill="#00C890" stroke="#081838" strokeWidth="2" />
    <text x="58" y="24" fontFamily="'Gilroy-Bold', sans-serif" fontSize="11" fontWeight="800" fill="#081838" textAnchor="middle">1</text>
  </svg>
);

const SellerShip = () => (
  <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <path d="M40 10 C 28 10 20 16 20 30 C 20 50 28 62 40 68 C 52 62 60 50 60 30 C 60 16 52 10 40 10 Z" fill="#FAF6EE" stroke="#081838" strokeWidth="2" />
    <path d="M40 28 C 35 21 22 25 22 36 C 22 48 40 56 40 56 C 40 56 58 48 58 36 C 58 25 45 21 40 28 Z" fill="#00C890" stroke="#081838" strokeWidth="2" />
    <text x="40" y="44" fontFamily="'Gilroy-Bold', sans-serif" fontSize="14" fontWeight="800" fill="#081838" textAnchor="middle">£</text>
  </svg>
);

const BUYER_STEPS = [
  { Illu: BuyerFind, titleId: 'CustomLandingPage.howItWorks.buyer.find.title', descriptionId: 'CustomLandingPage.howItWorks.buyer.find.description' },
  { Illu: BuyerSave, titleId: 'CustomLandingPage.howItWorks.buyer.save.title', descriptionId: 'CustomLandingPage.howItWorks.buyer.save.description' },
  { Illu: BuyerPay, titleId: 'CustomLandingPage.howItWorks.buyer.pay.title', descriptionId: 'CustomLandingPage.howItWorks.buyer.pay.description' },
  { Illu: BuyerReceive, titleId: 'CustomLandingPage.howItWorks.buyer.receive.title', descriptionId: 'CustomLandingPage.howItWorks.buyer.receive.description' },
  { Illu: BuyerReview, titleId: 'CustomLandingPage.howItWorks.buyer.review.title', descriptionId: 'CustomLandingPage.howItWorks.buyer.review.description' },
];

const SELLER_STEPS = [
  { Illu: SellerList, titleId: 'CustomLandingPage.howItWorks.seller.list.title', descriptionId: 'CustomLandingPage.howItWorks.seller.list.description' },
  { Illu: SellerBank, titleId: 'CustomLandingPage.howItWorks.seller.bank.title', descriptionId: 'CustomLandingPage.howItWorks.seller.bank.description' },
  { Illu: SellerNotified, titleId: 'CustomLandingPage.howItWorks.seller.notified.title', descriptionId: 'CustomLandingPage.howItWorks.seller.notified.description' },
  { Illu: SellerShip, titleId: 'CustomLandingPage.howItWorks.seller.ship.title', descriptionId: 'CustomLandingPage.howItWorks.seller.ship.description' },
  // Reviews close the seller journey too, so both tracks now run five steps and
  // share the review illustration.
  { Illu: BuyerReview, titleId: 'CustomLandingPage.howItWorks.seller.review.title', descriptionId: 'CustomLandingPage.howItWorks.seller.review.description' },
];

const StepCard = ({ Illu, index, titleId, descriptionId }) => (
  <div className={css.step}>
    <div className={css.illu}>
      <Illu />
    </div>
    <div className={css.stepNum}>{String(index + 1).padStart(2, '0')}</div>
    <h3 className={css.stepTitle}>
      <FormattedMessage id={titleId} />
    </h3>
    <p className={css.stepText}>
      <FormattedMessage id={descriptionId} />
    </p>
  </div>
);

const HowItWorksSection = () => {
  const intl = useIntl();
  const [tab, setTab] = useState('buyer');
  const isBuyer = tab === 'buyer';
  const steps = isBuyer ? BUYER_STEPS : SELLER_STEPS;

  return (
    <div className={css.root}>
      <div
        className={css.tabs}
        role="tablist"
        aria-label={intl.formatMessage({ id: 'CustomLandingPage.howItWorks.ariaLabel' })}
      >
        <button
          type="button"
          role="tab"
          aria-selected={isBuyer}
          className={classNames(css.tab, { [css.tabActive]: isBuyer })}
          onClick={() => setTab('buyer')}
        >
          <span className={css.tabBadge}>
            <FormattedMessage id="CustomLandingPage.howItWorks.badge.buyer" />
          </span>
          <FormattedMessage id="CustomLandingPage.howItWorks.tab.buyer" />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isBuyer}
          className={classNames(css.tab, { [css.tabActive]: !isBuyer })}
          onClick={() => setTab('seller')}
        >
          <span className={css.tabBadge}>
            <FormattedMessage id="CustomLandingPage.howItWorks.badge.seller" />
          </span>
          <FormattedMessage id="CustomLandingPage.howItWorks.tab.seller" />
        </button>
      </div>

      {/* Both tracks are five steps now, so the grid needs no seller variant. */}
      <div className={css.grid}>
        {steps.map((step, i) => (
          <StepCard
            key={step.titleId}
            Illu={step.Illu}
            index={i}
            titleId={step.titleId}
            descriptionId={step.descriptionId}
          />
        ))}
      </div>

      <p className={css.profitNote}>
        <FormattedMessage id="CustomLandingPage.howItWorks.profitNote" />
      </p>
    </div>
  );
};

export default HowItWorksSection;
